/*
 * Ballr — Kit personaliser
 *
 * Draws the customer's name + number onto the product photo, warped into the
 * poster's perspective, and keeps the values on the form so they arrive as
 * line item properties on the order.
 *
 * Geometry model
 * --------------
 * Each configured photo carries the poster's four corners (quad) as fractions
 * of the photo. Text is positioned in "poster space" — a flat 0..1 x 0..1 box —
 * and a homography maps that box onto the quad. The homography is emitted as a
 * CSS matrix3d so the browser does the warping, which keeps text crisp at any
 * size and costs nothing to animate while typing.
 */
(function () {
  'use strict';

  var POSTER_W = 1000; // reference px for poster space; the warp rescales it

  /* ---------- linear algebra ---------- */

  // Solve A x = b by Gaussian elimination with partial pivoting.
  function solve(A, b) {
    var n = b.length;
    var i;
    var j;
    var k;
    for (i = 0; i < n; i++) {
      var piv = i;
      for (j = i + 1; j < n; j++) {
        if (Math.abs(A[j][i]) > Math.abs(A[piv][i])) piv = j;
      }
      if (Math.abs(A[piv][i]) < 1e-12) return null;
      var tmpRow = A[i]; A[i] = A[piv]; A[piv] = tmpRow;
      var tmpVal = b[i]; b[i] = b[piv]; b[piv] = tmpVal;
      for (j = i + 1; j < n; j++) {
        var f = A[j][i] / A[i][i];
        for (k = i; k < n; k++) A[j][k] -= f * A[i][k];
        b[j] -= f * b[i];
      }
    }
    var x = new Array(n);
    for (i = n - 1; i >= 0; i--) {
      var s = b[i];
      for (j = i + 1; j < n; j++) s -= A[i][j] * x[j];
      x[i] = s / A[i][i];
    }
    return x;
  }

  // Homography mapping the unit square corners to dst (TL, TR, BR, BL), in px.
  function homography(dst) {
    var src = [[0, 0], [1, 0], [1, 1], [0, 1]];
    var A = [];
    var b = [];
    for (var i = 0; i < 4; i++) {
      var x = src[i][0];
      var y = src[i][1];
      var u = dst[i][0];
      var v = dst[i][1];
      A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
      b.push(u);
      A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
      b.push(v);
    }
    return solve(A, b); // [a,b,c,d,e,f,g,h] with i = 1
  }

  // CSS matrix3d is column-major; a 2D homography lives in the x/y/w slots.
  function matrix3d(h, layerW, layerH) {
    if (!h) return 'none';
    // Compose with the scale that turns layer px into unit poster coordinates.
    var a = h[0] / layerW;
    var b = h[1] / layerH;
    var c = h[2];
    var d = h[3] / layerW;
    var e = h[4] / layerH;
    var f = h[5];
    var g = h[6] / layerW;
    var i = h[7] / layerH;
    return 'matrix3d(' +
      [a, d, 0, g,
       b, e, 0, i,
       0, 0, 1, 0,
       c, f, 0, 1].join(',') + ')';
  }

  /* ---------- text measuring ---------- */

  var measureCanvas = document.createElement('canvas');
  var measureCtx = measureCanvas.getContext('2d');

  function fontCss(size, family, weight) {
    return weight + ' ' + size + 'px "' + family + '", sans-serif';
  }

  function isDigit(ch) { return ch >= '0' && ch <= '9'; }

  /* ---------- optical kerning between digits ----------

     None of the kit fonts carries a kerning table, so every digit sits in the
     middle of its own advance and a narrow one — "1" above all — leaves a hole
     beside its neighbour: "10" reads as "1 0", which is what a customer
     reported. BALLR closes that by eye when it sets a poster, so we do the
     same from the outlines: rasterise both digits, measure the white area
     between their contours, and pull them together until that area matches
     what the font leaves between two zeros — the spacing the printed "00"
     placeholder shows. A pair the font already sets tightly is left exactly as
     it was, because the gap is only ever closed, never opened. */

  var KERN_PX = 120;      // raster height the contours are read at
  var KERN_DEPTH = 0.45;  // how deep into a notch white still counts, in cap heights
  var kernCanvas = null, kernCtx = null;
  var shapeCache = {}, gapCache = {};

  // Until the kit font has actually loaded, every measurement is of the
  // fallback face — usable, but never worth caching.
  function fontReady(family, weight) {
    if (!document.fonts || !document.fonts.check) return true;
    try { return document.fonts.check(fontCss(KERN_PX, family, weight)); }
    catch (e) { return true; }
  }

  // Per-row ink edges of one character, relative to its own ink box, plus the
  // side bearings that put it back inside the font's advance.
  function glyphShape(ch, family, weight) {
    var key = family + '|' + weight + '|' + ch;
    if (shapeCache[key] !== undefined) return shapeCache[key];
    if (!kernCtx) {
      kernCanvas = document.createElement('canvas');
      kernCanvas.width = KERN_PX * 3;
      kernCanvas.height = KERN_PX * 3;
      try { kernCtx = kernCanvas.getContext('2d', { willReadFrequently: true }); }
      catch (e) { kernCtx = kernCanvas.getContext('2d'); }
      if (!kernCtx) return null;
    }
    var W = kernCanvas.width, H = kernCanvas.height, originX = KERN_PX;
    kernCtx.clearRect(0, 0, W, H);
    kernCtx.font = fontCss(KERN_PX, family, weight);
    kernCtx.textBaseline = 'alphabetic';
    kernCtx.fillStyle = '#fff';
    kernCtx.fillText(ch, originX, KERN_PX * 2);
    var adv = kernCtx.measureText(ch).width;
    var data;
    try { data = kernCtx.getImageData(0, 0, W, H).data; }
    catch (e) { return null; }
    var rows = [], top = -1, bottom = -1, minX = W, maxX = -1;
    for (var y = 0; y < H; y++) {
      var first = -1, last = -1, row = y * W * 4 + 3;
      for (var x = 0; x < W; x++) {
        if (data[row + x * 4] > 128) { if (first < 0) first = x; last = x; }
      }
      rows.push(first < 0 ? null : [first, last]);
      if (first >= 0) {
        if (top < 0) top = y;
        bottom = y;
        if (first < minX) minX = first;
        if (last > maxX) maxX = last;
      }
    }
    if (maxX < 0) return null;
    for (var i = top; i <= bottom; i++) {
      if (rows[i]) { rows[i][0] -= minX; rows[i][1] -= minX; }
    }
    var shape = {
      rows: rows,
      top: top,
      bottom: bottom,
      width: maxX - minX + 1,
      lsb: minX - originX,
      rsb: adv - (maxX + 1 - originX)
    };
    if (fontReady(family, weight)) shapeCache[key] = shape;
    return shape;
  }

  // White between two outlines when b's ink starts `gap` px after a's ends.
  // A notch counts only `depth` deep, or a glyph like "1" would read as pure
  // space above its foot and swallow its neighbour.
  function whiteArea(a, b, gap, depth) {
    var top = Math.min(a.top, b.top);
    var bottom = Math.max(a.bottom, b.bottom);
    var total = 0;
    for (var y = top; y <= bottom; y++) {
      var ra = a.rows[y], lb = b.rows[y];
      if (!ra || !lb) { total += depth; continue; }
      var d = (a.width + gap + lb[0]) - (ra[1] + 1);
      total += d > depth ? depth : (d > 0 ? d : 0);
    }
    return total;
  }

  // The ink gap to leave between two digits, in em. Null when the outlines
  // can't be read (no canvas, font not there) — the caller then keeps the
  // font's own advance.
  function opticalGap(a, b, family, weight) {
    var key = family + '|' + weight + '|' + a + b;
    if (gapCache[key] !== undefined) return gapCache[key];
    var ga = glyphShape(a, family, weight);
    var gb = glyphShape(b, family, weight);
    var gz = glyphShape('0', family, weight);
    if (!ga || !gb || !gz) return null;
    var depth = (gz.bottom - gz.top + 1) * KERN_DEPTH;
    var target = whiteArea(gz, gz, gz.rsb + gz.lsb, depth);
    var natural = ga.rsb + gb.lsb;
    var lo = -KERN_PX * 0.6, hi = natural;
    for (var i = 0; i < 24; i++) {
      var mid = (lo + hi) / 2;
      if (whiteArea(ga, gb, mid, depth) < target) lo = mid; else hi = mid;
    }
    var gap = (lo + hi) / 2;
    if (gap > natural) gap = natural;
    var em = gap / KERN_PX;
    if (fontReady(family, weight)) gapCache[key] = em;
    return em;
  }

  /* Lay a string out: the font's advances plus `tracking`, with every digit
     pair pulled in to its optical gap — or to an artwork-measured one, when
     the product's config names that pair in `kern` (ink gap in em). Returns
     one x per character and the ink box they make. */
  function layoutRun(text, size, family, weight, tracking, kern) {
    measureCtx.font = fontCss(size, family, weight);
    var whole = measureCtx.measureText(text);
    var ms = text.split('').map(function (ch) {
      var m = measureCtx.measureText(ch);
      var right = m.actualBoundingBoxRight;
      return {
        ch: ch,
        adv: m.width,
        left: m.actualBoundingBoxLeft || 0,
        right: (right == null ? m.width : right)
      };
    });
    var track = tracking * size;
    var xs = [], x = 0;
    for (var i = 0; i < ms.length; i++) {
      xs.push(x);
      if (i === ms.length - 1) break;
      var step = ms[i].adv;
      if (isDigit(ms[i].ch) && isDigit(ms[i + 1].ch)) {
        var pair = ms[i].ch + ms[i + 1].ch;
        var set = kern && kern[pair] != null;
        var g = set ? kern[pair] : opticalGap(ms[i].ch, ms[i + 1].ch, family, weight);
        if (g != null) {
          var tight = ms[i].right + g * size + ms[i + 1].left;
          if (set || tight < step) step = tight;
        }
      }
      x += step + track;
    }
    var last = ms.length - 1;
    return {
      xs: xs,
      ascent: whole.actualBoundingBoxAscent || size * 0.72,
      descent: whole.actualBoundingBoxDescent || 0,
      inkLeft: last < 0 ? 0 : -ms[0].left,
      inkRight: last < 0 ? 0 : xs[last] + ms[last].right,
      width: last < 0 ? 0 : xs[last] + ms[last].right + ms[0].left
    };
  }

  // Size the font so the ink height matches capPx, then shrink to fit maxWpx.
  function fitFont(text, capPx, maxWpx, family, weight, tracking, kern) {
    var size = capPx * 1.4;
    var run;
    for (var pass = 0; pass < 8; pass++) {
      run = layoutRun(text, size, family, weight, tracking, kern);
      var h = run.ascent + run.descent;
      if (!h) break;
      if (Math.abs(h - capPx) < 0.3) break;
      size = size * (capPx / h);
    }
    run = layoutRun(text, size, family, weight, tracking, kern);
    if (run.width > maxWpx && run.width > 0) {
      size = size * (maxWpx / run.width);
      run = layoutRun(text, size, family, weight, tracking, kern);
    }
    run.size = size;
    return run;
  }

  /* ---------- painted image rect (accounts for object-fit) ---------- */

  function paintedRect(img) {
    var w = img.clientWidth;
    var h = img.clientHeight;
    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    if (!w || !h) return null;
    // Not decoded yet (Dawn lazy-loads secondary media) — assume it fills its box.
    if (!nw || !nh) {
      return { left: img.offsetLeft, top: img.offsetTop, width: w, height: h };
    }
    var fit = window.getComputedStyle(img).objectFit || 'fill';
    if (fit !== 'contain' && fit !== 'cover' && fit !== 'scale-down') {
      return { left: img.offsetLeft, top: img.offsetTop, width: w, height: h };
    }
    var scale = fit === 'cover'
      ? Math.max(w / nw, h / nh)
      : Math.min(w / nw, h / nh);
    if (fit === 'scale-down') scale = Math.min(scale, 1);
    var pw = nw * scale;
    var ph = nh * scale;
    return {
      left: img.offsetLeft + (w - pw) / 2,
      top: img.offsetTop + (h - ph) / 2,
      width: pw,
      height: ph
    };
  }

  /* ---------- one product photo ---------- */

  function Stage(mediaCfg, cfg, container, sourceImg) {
    this.cfg = cfg;
    this.mediaCfg = mediaCfg;
    this.sourceImg = sourceImg;

    var stage = document.createElement('div');
    stage.className = 'bkp-stage';

    var base = document.createElement('img');
    base.className = 'bkp-stage__base';
    base.alt = '';
    base.decoding = 'async';
    // Fetch eagerly: until this paints, the shopper sees the original photo
    // with its NAME/00 placeholder showing through.
    base.loading = 'eager';
    if ('fetchPriority' in base) base.fetchPriority = 'high';
    base.src = mediaCfg.base;
    base.style.objectFit = window.getComputedStyle(sourceImg).objectFit || 'cover';
    stage.appendChild(base);

    // An SVG layer lets us set the text baseline exactly, instead of inferring
    // it from CSS line-box metrics (which vary per font).
    var SVGNS = 'http://www.w3.org/2000/svg';
    var layerH = POSTER_W * (cfg.aspect || 1.3333);
    var layer = document.createElementNS(SVGNS, 'svg');
    layer.setAttribute('class', 'bkp-layer');
    layer.setAttribute('viewBox', '0 0 ' + POSTER_W + ' ' + layerH);
    layer.setAttribute('width', POSTER_W);
    layer.setAttribute('height', layerH);
    layer.setAttribute('aria-hidden', 'true');
    stage.appendChild(layer);

    this.nodes = {};
    this.draw = {};
    var self = this;
    (cfg.fields || []).forEach(function (f) {
      if (!f.draw) return; // input-only field: collected, but not printed on the photo
      var el = document.createElementNS(SVGNS, 'text');
      el.setAttribute('class', 'bkp-text');
      el.setAttribute('fill', f.draw.color || '#fff');
      el.setAttribute('text-anchor', 'start');
      el.setAttribute('xml:space', 'preserve');
      layer.appendChild(el);
      self.nodes[f.key] = el;
      self.draw[f.key] = f.draw;
    });

    if (window.getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }
    container.appendChild(stage);

    this.el = stage;
    this.base = base;
    this.layer = layer;
    this.layerH = POSTER_W * (cfg.aspect || 1.3333);

    base.addEventListener('load', function () { stage.classList.add('bkp--ready'); });
    if (base.complete && base.naturalWidth) stage.classList.add('bkp--ready');
  }

  Stage.prototype.position = function () {
    var r = paintedRect(this.sourceImg);
    if (!r) return false;
    this.el.style.left = r.left + 'px';
    this.el.style.top = r.top + 'px';
    this.el.style.width = r.width + 'px';
    this.el.style.height = r.height + 'px';

    var quad = this.mediaCfg.quad.map(function (p) {
      return [p[0] * r.width, p[1] * r.height];
    });
    this.layer.style.width = POSTER_W + 'px';
    this.layer.style.height = this.layerH + 'px';
    this.layer.style.transform = matrix3d(homography(quad), POSTER_W, this.layerH);
    return true;
  };

  Stage.prototype.setText = function (values) {
    var cfg = this.cfg;
    var layerH = this.layerH;
    var self = this;
    Object.keys(this.nodes).forEach(function (key) {
      var el = self.nodes[key];
      var f = self.draw[key];
      var text = (values[key] || '').toUpperCase();
      if (!text) { el.setAttribute('display', 'none'); return; }
      el.removeAttribute('display');

      var tracking = f.tracking || 0;
      var fit = fitFont(
        text,
        f.h * layerH,
        (f.maxW || 0.9) * POSTER_W,
        cfg.font,
        cfg.weight || 400,
        tracking,
        f.kern
      );

      el.textContent = text;
      el.setAttribute('font-family', '"' + cfg.font + '", sans-serif');
      el.setAttribute('font-weight', cfg.weight || 400);
      el.setAttribute('font-size', fit.size);
      // Every character carries its own x, so the spacing is the one we laid
      // out — letter-spacing would add to it.
      el.setAttribute('letter-spacing', 0);
      // Centre the ink box — not the em box — on the configured point.
      var x0 = f.cx * POSTER_W - (fit.inkLeft + fit.inkRight) / 2;
      el.setAttribute('x', fit.xs.map(function (v) { return v + x0; }).join(' '));
      el.setAttribute('y', f.cy * layerH + (fit.ascent - fit.descent) / 2);
    });
  };

  Stage.prototype.destroy = function () {
    if (this.ro) { this.ro.disconnect(); this.ro = null; }
    if (this.el && this.el.parentNode) this.el.parentNode.removeChild(this.el);
  };

  /* True when a gallery mutation was not made by a stage of ours. */
  function isForeignMutation(record) {
    if (record.target.closest && record.target.closest('.bkp-stage')) return false;
    var nodes = Array.prototype.slice.call(record.addedNodes)
      .concat(Array.prototype.slice.call(record.removedNodes));
    return nodes.some(function (n) {
      return !(n.nodeType === 1 && n.classList && n.classList.contains('bkp-stage'));
    });
  }

  /* ---------- the block ---------- */

  /* Build a working config from a family layout. A product on a layout carries
     no geometry of its own — only its ink colour, and optionally a clean
     artwork file that lets us skip reconstructing one. */
  function configFromLayout(root) {
    var key = root.getAttribute('data-layout');
    if (!key || !window.BallrKitLayouts || !window.BallrKitLayouts[key]) return null;
    var layout = window.BallrKitLayouts[key];
    var ink = root.getAttribute('data-ink') || '#FFFFFF';
    var inkSub = root.getAttribute('data-ink-sub') || '#141414';
    var mediaId = parseInt(root.getAttribute('data-media-id'), 10);
    var src = root.getAttribute('data-clean') || root.getAttribute('data-media-src');
    if (!mediaId || !src) return null;

    var fields = layout.fields.map(function (f) {
      var draw = JSON.parse(JSON.stringify(f.draw));
      draw.color = f.key === 'subtitle' ? inkSub : ink;
      return { key: f.key, label: f.label, property: f.property, type: f.type,
               max: f.max, placeholder: f.placeholder, hint: f.hint, draw: draw };
    });
    return {
      _layout: key,
      _needsErase: !root.getAttribute('data-clean'),
      _src: src,
      aspect: layout.aspect,
      font: 'Squada One',
      weight: 400,
      fields: fields,
      media: [{ id: mediaId, base: src, quad: layout.quad }]
    };
  }

  /* A calibrated product keeps its geometry in the metafield JSON, but the
     three things a shop owner might want to change — text colour, kit font and
     the clean artwork — are editable as ordinary product fields. Anything the
     client actually filled in wins over the baked-in value. */
  function applyProductOverrides(root, cfg) {
    if (!cfg || !cfg.fields) return;

    var ink = root.getAttribute('data-ink-set');
    var inkSub = root.getAttribute('data-ink-sub-set');
    if (ink || inkSub) {
      cfg.fields.forEach(function (f) {
        if (!f.draw) return;
        var v = f.key === 'subtitle' ? inkSub : ink;
        if (v) f.draw.color = v;
      });
    }

    var font = root.getAttribute('data-font-set');
    if (font) cfg.font = font;

    // The clean file stands in for the photo shown first. The white frame is a
    // different shot, so it has its own field; the config marks which media
    // entry that is, because it is not always the second one.
    var clean = root.getAttribute('data-clean');
    if (clean && cfg.media && cfg.media.length) {
      cfg.media[0].base = clean;
      cfg._needsErase = false;
    }

    var cleanWhite = root.getAttribute('data-clean-white');
    if (cleanWhite && cfg.media) {
      cfg.media.forEach(function (m) {
        if (m.frame === 'white') m.base = cleanWhite;
      });
    }
  }

  function Personaliser(root) {
    this.root = root;
    try {
      this.cfg = JSON.parse(root.getAttribute('data-config'));
    } catch (e) {
      this.cfg = null;
    }
    if (!this.cfg || !this.cfg.fields) {
      var built = configFromLayout(root);
      if (built) this.cfg = built;
    } else {
      applyProductOverrides(root, this.cfg);
    }
    if (!this.cfg) this.cfg = {};

    this.sectionId = root.getAttribute('data-section-id');
    this.require = root.getAttribute('data-require') !== 'false';
    // Theme setting: with the preview off nothing is drawn on the photo, but
    // the fields, the validation and the order properties are untouched.
    this.preview = root.getAttribute('data-preview') !== 'false';
    this.errorEl = root.querySelector('[data-bkp-error]');
    this.stages = [];
    this.inputs = [];

    var self = this;

    root.querySelectorAll('[data-bkp-field]').forEach(function (input) {
      var key = input.getAttribute('data-bkp-field');
      var type = input.getAttribute('data-bkp-type');
      self.inputs.push({ key: key, type: type, el: input });
      input.addEventListener('input', function () {
        var clean = type === 'number'
          ? this.value.replace(/\D/g, '')
          : this.value.replace(/[^A-Za-z0-9 .'&-]/g, '').toUpperCase();
        if (clean !== this.value) this.value = clean;
        self.render();
        // Clear the "add a name first" warning as soon as it stops being true,
        // instead of leaving it under a filled-in form.
        if (self.errorEl && self.errorEl.classList.contains('bkp--show') &&
            !self.missing().length) {
          self.showError(false);
        }
      });
    });

    this.bindPreview();
    this.bindForm();
    this.mount();
    this.autoErase();

    // Dawn swaps gallery markup on variant change; re-mount when it does.
    var wrapper = document.getElementById('MediaGallery-' + this.sectionId) ||
      document.querySelector('.product__media-wrapper');
    if (wrapper) {
      this.observer = new MutationObserver(function (records) {
        // The stages live inside the gallery too, so ignore our own work: every
        // render rewrites the SVG text, and treating that as "the gallery changed"
        // remounts the stages, which renders again - a loop that rebuilt the
        // overlay ~30 times a second for as long as the fields were filled.
        if (!records.some(isForeignMutation)) return;
        clearTimeout(self.remountTimer);
        self.remountTimer = setTimeout(function () { self.mount(); }, 60);
      });
      this.observer.observe(wrapper, { childList: true, subtree: true });
    }

    window.addEventListener('resize', function () {
      clearTimeout(self.resizeTimer);
      self.resizeTimer = setTimeout(function () { self.reflow(); }, 100);
    });

    if (this.preview && document.fonts && document.fonts.load && this.cfg.font) {
      document.fonts
        .load((this.cfg.weight || 400) + ' 100px "' + this.cfg.font + '"')
        .then(function () { self.render(); })
        .catch(function () {});
    }
  }

  Personaliser.prototype.mount = function () {
    var self = this;
    this.stages.forEach(function (s) { s.destroy(); });
    this.stages = [];
    if (!this.preview) return;
    if (!this.cfg.media || !this.cfg.media.length) return;

    this.cfg.media.forEach(function (m, mi) {
      var item = document.querySelector('[data-media-id="' + self.sectionId + '-' + m.id + '"]');

      /* A config copied from another product keeps that product's media ids, and
         a media id that matches nothing used to mean no stage at all — the
         shopper types and the photo simply never changes, with nothing to say
         why. The photos are shot to a fixed template, so falling back to the
         gallery photo in the same position is nearly always what was meant.
         Warn loudly so the ids still get corrected (autocal.py does that). */
      if (!item) {
        var gallery = document.querySelectorAll('[data-media-id^="' + self.sectionId + '-"]');
        if (gallery[mi]) {
          item = gallery[mi];
          if (window.console && console.warn) {
            console.warn('[ballr] kit_personalisation media id ' + m.id +
              ' is not on this product — using gallery photo #' + (mi + 1) +
              ' instead. Update media[' + mi + '].id in the product metafield.');
          }
        }
      }
      if (!item) return;
      var container = item.querySelector('.product__media') || item;
      var img = container.querySelector('img');
      if (!img) return;

      // Mount straight away — Dawn lazy-loads secondary media, so waiting for
      // the source image to decode would leave those photos un-personalised.
      var stage = new Stage(m, self.cfg, container, img);
      self.stages.push(stage);
      stage.position();
      self.render();
      if (!img.complete || !img.naturalWidth) {
        img.addEventListener('load', function () { stage.position(); self.render(); }, { once: true });
      }
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(function () { stage.position(); self.render(); });
        ro.observe(img);
        stage.ro = ro;
      }
    });
  };

  /* No clean artwork on file: rebuild one in the browser, then swap it in. */
  Personaliser.prototype.autoErase = function () {
    var cfg = this.cfg, self = this;
    if (!this.preview) return;
    if (!cfg._needsErase || !window.BallrAutoErase) return;
    var layout = JSON.parse(JSON.stringify(window.BallrKitLayouts[cfg._layout]));

    /* Full-bleed designs place the name and number differently on every club's
       artwork, so measure this one rather than assume the family default. */
    var ready = (cfg._layout === 'fullbleed' && window.BallrDetectPlaceholder)
      ? window.BallrDetectPlaceholder(cfg._src, layout.quad).then(function (found) {
          if (!found) return;
          var room = function (cx) { return 2 * Math.min(cx, 1 - cx) * 0.92; };
          [['name', found.name], ['number', found.number]].forEach(function (pair) {
            var f = cfg.fields.filter(function (x) { return x.key === pair[0]; })[0];
            if (!f) return;
            var m = pair[1];
            f.draw.cx = m.cx; f.draw.cy = m.cy; f.draw.h = m.h;
            var wide = pair[0] === 'name' ? Math.max(m.w * 1.9, 0.55) : m.w * 1.06;
            f.draw.maxW = Math.min(wide, room(m.cx), 0.82);
          });
          var top = Math.max(0, found.name.cy - found.name.h / 2 - 0.045);
          var bot = Math.min(1, found.number.cy + found.number.h / 2 + 0.045);
          var half = Math.max(found.name.w, found.number.w) / 2 + 0.05;
          layout.erase = [[Math.max(0, 0.5 - half), top, Math.min(1, 0.5 + half), bot]];
        })
      : Promise.resolve();

    ready.then(function () {
      return window.BallrAutoErase(cfg._src, layout, cfg._layout);
    })
      .then(function (dataUrl) {
        cfg.media[0].base = dataUrl;
        self.stages.forEach(function (st) {
          if (st.base) st.base.src = dataUrl;
        });
      })
      .catch(function () {
        /* Leave the original photo in place — the fields still work, the
           shopper just sees the sample name until artwork is supplied. */
      });
  };

  Personaliser.prototype.reflow = function () {
    this.stages.forEach(function (s) { s.position(); });
    if (this.modalStage) this.modalStage.position();
    this.render();
  };

  Personaliser.prototype.values = function () {
    var out = {};
    this.inputs.forEach(function (i) { out[i.key] = i.el.value.trim(); });
    return out;
  };

  Personaliser.prototype.missing = function () {
    return this.inputs.filter(function (i) { return !i.el.value.trim(); });
  };

  Personaliser.prototype.render = function () {
    var v = this.values();
    this.stages.forEach(function (s) { s.setText(v); });
    if (this.modalStage) this.modalStage.setText(v);
  };

  Personaliser.prototype.showError = function (show) {
    if (this.errorEl) this.errorEl.classList.toggle('bkp--show', show);
    this.inputs.forEach(function (i) {
      i.el.classList.toggle('bkp--invalid', show && !i.el.value.trim());
    });
  };

  /* ---------- full-size preview ---------- */

  /* Which photo the shopper is actually looking at — that is what the big
     preview has to show. "Has a size" is not enough: on a phone the gallery is
     a slider, so every slide is laid out and only one is scrolled into view.
     Score each stage by how much of it falls inside the gallery's own box. */
  Personaliser.prototype.visibleStage = function () {
    var host = document.getElementById('MediaGallery-' + this.sectionId) ||
      document.querySelector('.product__media-wrapper');
    var hr = host && host.getBoundingClientRect();
    var best = null;
    var bestArea = 0;
    this.stages.forEach(function (s) {
      var r = s.el.getBoundingClientRect();
      if (r.width < 40 || r.height < 40) return;
      var area = r.width * r.height;
      if (hr) {
        var w = Math.max(0, Math.min(r.right, hr.right) - Math.max(r.left, hr.left));
        var h = Math.max(0, Math.min(r.bottom, hr.bottom) - Math.max(r.top, hr.top));
        area = w * h;
      }
      if (area > bestArea) { bestArea = area; best = s; }
    });
    return best || this.stages[0] || null;
  };

  Personaliser.prototype.bindPreview = function () {
    var self = this;
    if (!this.preview) return;
    this.modal = this.root.querySelector('[data-bkp-modal]');
    var btn = this.root.querySelector('[data-bkp-preview]');
    if (!this.modal || !btn) return;

    // Dawn's product info column establishes a containing block, so a `fixed`
    // overlay left inside it covers only that column. Move it to the body.
    var uid = this.root.id;
    if (uid) {
      document.querySelectorAll('[data-bkp-portal="' + uid + '"]').forEach(function (el) {
        el.parentNode.removeChild(el);
      });
      this.modal.setAttribute('data-bkp-portal', uid);
    }
    document.body.appendChild(this.modal);

    this.modalStageHost = this.modal.querySelector('[data-bkp-modal-stage]');

    btn.addEventListener('click', function () { self.openPreview(); });
    this.modal.querySelectorAll('[data-bkp-close]').forEach(function (el) {
      el.addEventListener('click', function () { self.closePreview(); });
    });
    // clicking the dim around the card closes it too
    this.modal.addEventListener('click', function (e) {
      if (e.target === self.modal) self.closePreview();
    });
    // Adding to cart from inside the preview should not leave it open over the
    // cart drawer that Dawn slides in.
    var add = this.modal.querySelector('.bkp-preview__add');
    if (add) add.addEventListener('click', function () { setTimeout(function () { self.closePreview(); }, 400); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.modal.hasAttribute('hidden')) self.closePreview();
    });
  };

  Personaliser.prototype.openPreview = function () {
    var self = this;
    if (this.require) {
      var missing = this.missing();
      if (missing.length) {
        this.showError(true);
        missing[0].el.focus();
        return;
      }
      this.showError(false);
    }

    var from = this.visibleStage();
    var mediaCfg = from ? from.mediaCfg : (this.cfg.media || [])[0];
    if (!mediaCfg) return;

    if (this.modalStage) { this.modalStage.destroy(); this.modalStage = null; }
    this.modalStageHost.innerHTML = '';

    // The stage lays itself out over an image, so give it one: the same clean
    // base, invisible, purely to define the box the poster is drawn into.
    var ghost = document.createElement('img');
    ghost.className = 'bkp-preview__ghost';
    ghost.alt = '';
    ghost.src = mediaCfg.base;
    this.modalStageHost.appendChild(ghost);

    this.modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    var build = function () {
      if (self.modalStage) self.modalStage.destroy();
      self.modalStage = new Stage(mediaCfg, self.cfg, self.modalStageHost, ghost);
      self.modalStage.el.style.zIndex = 1;
      self.modalStage.position();
      self.modalStage.setText(self.values());
    };
    if (ghost.complete && ghost.naturalWidth) build();
    else ghost.addEventListener('load', build, { once: true });
  };

  Personaliser.prototype.closePreview = function () {
    if (!this.modal) return;
    this.modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    if (this.modalStage) { this.modalStage.destroy(); this.modalStage = null; }
    if (this.modalStageHost) this.modalStageHost.innerHTML = '';
  };

  Personaliser.prototype.bindForm = function () {
    if (!this.require) return;
    var self = this;
    var form = document.getElementById('product-form-' + this.sectionId) ||
      (this.inputs[0] && this.inputs[0].el.form);
    if (!form || !this.inputs.length) return;
    this.form = form;

    var block = function (e) {
      var missing = self.missing();
      if (!missing.length) { self.showError(false); return; }
      e.preventDefault();
      e.stopImmediatePropagation();
      self.showError(true);
      missing[0].el.focus();
      missing[0].el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Capture phase so we stop Dawn's submit handler before it posts to /cart/add.
    form.addEventListener('submit', block, true);

    // 2026-09-28: Buy it now never submits the form — Shopify's button builds
    // the cart and jumps to checkout on its own click — so it skipped the
    // guard and orders arrived with no name/number (#5884, #5802). Catch its
    // click on the way down, before it reaches the button.
    document.addEventListener('click', function (e) {
      if (!self.form || !document.contains(self.form)) return;
      var path = e.composedPath ? e.composedPath() : [e.target];
      for (var i = 0; i < path.length; i++) {
        var n = path[i];
        if (n.nodeType === 1 && n.matches('[data-shopify="payment-button"], shopify-buy-it-now-button, shopify-accelerated-checkout')) {
          if (self.form.contains(n)) block(e);
          return;
        }
      }
    }, true);
  };

  /* ---------- suppress a third-party options app on this template ---------- */

  /*
   * Tepo Product Options ("tcustomizer") is installed as a store-wide app embed:
   * it injects its own name/number fields, swaps the theme's add-to-cart
   * button for a clone of its own and hooks Buy-it-now. On this template the
   * personaliser replaces it, so strip its widget out to avoid two sets of
   * fields on one form. The snippet already hides this product from Tepo's
   * option sets (window._TC guard); this is the fallback for anything that
   * still gets through. The tidy long-term fix is turning Tepo's option set
   * off for these products.
   *
   * 2026-09-20: the watcher used to give up after 15 s, but Tepo re-installs
   * its clone after EVERY variant change, however late — a shopper who picked
   * a size after reading for a while got a clone that died on Tepo's own
   * "Some fields are required" check with nothing on screen. It now watches
   * for as long as the page lives and reacts to Tepo's nodes straight away
   * instead of after a debounce that a busy DOM could starve.
   */
  var SUPPRESS_SELECTORS = [
    '.tcustomizer-product-form',
    '.tcustomizer-customization-form',
    '.tcustomizer-data',
    '.tcustomizer-overlap-button',
    '[name="tcustomizer-add"]',
    '[name="tcustomizer-buy-now"]'
  ];
  var APP_NODE_SELECTOR = SUPPRESS_SELECTORS.join(', ') +
    ', .tcustomizer-hide-button, [data-shopify="payment-button"]';
  // Tepo hooks Buy-it-now with a capture-phase click handler on the payment
  // button wrapper that swallows the click. It skips a wrapper already carrying
  // its own marker, so plant the marker before it gets there.
  var PAYMENT_ATTACHED = 'data-tcustomizer-payment-attached';

  function suppressAppOptions() {
    var removed = 0;
    SUPPRESS_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.closest('[data-bkp-root]')) return;
        el.remove();
        removed++;
      });
    });
    // Give the theme's own button back.
    document.querySelectorAll('.product-form__submit.tcustomizer-hide-button')
      .forEach(function (btn) { btn.classList.remove('tcustomizer-hide-button'); });
    document.querySelectorAll('[data-shopify="payment-button"]:not([' + PAYMENT_ATTACHED + '])')
      .forEach(function (wrap) {
        wrap.setAttribute(PAYMENT_ATTACHED, 'true');
        wrap.setAttribute('data-bkp-guard', '');
      });
    return removed;
  }

  function touchesApp(node) {
    if (!node || node.nodeType !== 1) return false;
    return (node.matches && node.matches(APP_NODE_SELECTOR)) ||
      (node.querySelector && !!node.querySelector(APP_NODE_SELECTOR));
  }

  function isAppMutation(records) {
    for (var i = 0; i < records.length; i++) {
      var r = records[i];
      if (r.type === 'attributes') {
        if (touchesApp(r.target)) return true;
        continue;
      }
      for (var k = 0; k < r.addedNodes.length; k++) {
        if (touchesApp(r.addedNodes[k])) return true;
      }
    }
    return false;
  }

  function watchAppOptions() {
    suppressAppOptions();
    var obs = new MutationObserver(function (records) {
      if (isAppMutation(records)) suppressAppOptions();
    });
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /* ---------- boot ---------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-bkp-root]').forEach(function (el) {
      if (el.dataset.bkpBound) return;
      el.dataset.bkpBound = '1';
      new Personaliser(el);
      if (el.getAttribute('data-suppress-apps') !== 'false' && !window.__bkpSuppressing) {
        window.__bkpSuppressing = true;
        watchAppOptions();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) { init(e.target); });

  /* Quick add fetches the product page and injects the product form into a
     modal, long after this script has run. Without this, a kit added from a
     collection card would have no personalisation inputs at all. */
  var pending;
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if ((n.matches && n.matches('[data-bkp-root]')) ||
            (n.querySelector && n.querySelector('[data-bkp-root]'))) {
          clearTimeout(pending);
          pending = setTimeout(function () { init(); }, 60);
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

